import React, { useEffect, useMemo, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { getFestival, getProgram, isProgramOver, posterUrl } from '../../data/loadData'
import { capitalize, dateRange, lower, parseDay, upper } from '../../data/dates'
import './ProgramSection.css'

const CATEGORIES = [
    { id: 'oyun', label: 'Oyunlar', tag: 'Oyun' },
    { id: 'atolye', label: 'Atölyeler', tag: 'Atölye' },
    { id: 'soylesi', label: 'Söyleşiler', tag: 'Söyleşi' },
    { id: 'cocuk', label: 'Çocuk', tag: 'Çocuk' },
]

const filled = (value) => value !== undefined && value !== null && String(value).trim() !== ''

function categoryOf(show) {
    const tur = lower(show.tur)
    if (tur.includes('atölye')) return 'atolye'
    if (/panel|konferans|söyleşi/.test(tur)) return 'soylesi'
    if (tur.includes('çocuk') || lower(show.yas) === 'çocuk') return 'cocuk'
    return 'oyun'
}

function startMinutes(saat) {
    const match = String(saat || '').match(/(\d{1,2})[:.](\d{2})/)
    return match ? Number(match[1]) * 60 + Number(match[2]) : 24 * 60
}

function splitTime(saat) {
    const [start, ...rest] = String(saat || '').split(/\s*[-–]\s*/)
    return { start: start || '', end: rest.join(' – ') }
}

const looksLikeHtml = (value) => /<[a-z][\s\S]*>/i.test(value || '')

function plainText(value) {
    if (!looksLikeHtml(value) && !/&[a-z#0-9]+;/i.test(value || '')) return String(value || '')
    if (typeof DOMParser === 'undefined') return String(value || '').replace(/<[^>]+>/g, ' ')
    return new DOMParser().parseFromString(String(value), 'text/html').body.textContent || ''
}

function describe(aciklama) {
    const parts = plainText(aciklama).split('*').map((part) => part.trim()).filter(Boolean)
    const capacityNote = parts.find((part) => /^kontenjan\s*:/i.test(part))
    return {
        summary: parts[0] || '',
        capacity: capacityNote ? capacityNote.replace(/^kontenjan\s*:\s*/i, '').split('.')[0] : '',
    }
}

function todayIso() {
    const now = new Date()
    const pad = (n) => String(n).padStart(2, '0')
    return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`
}

export default function ProgramSection({ year = 2022 }) {
    const program = getProgram(year)
    const festival = getFestival(year)
    const [filter, setFilter] = useState('all')
    const [activeDay, setActiveDay] = useState(null)
    const railRef = useRef(null)

    const events = useMemo(() => program.events || [], [program])

    const days = useMemo(() => {
        const dates = program.dates || []
        if (!dates.length) {
            return [{ id: `fp-${year}-all`, label: null, events }]
        }
        return dates.map((date, index) => {
            const dayEvents = events
                .filter((show) => show.tarih === date.date)
                .map((show, order) => ({ show, order }))
                .sort((a, b) => startMinutes(a.show.saat) - startMinutes(b.show.saat) || a.order - b.order)
                .map(({ show }) => show)
            const label = parseDay(date)
            return {
                id: `fp-${year}-${index}`,
                iso: date.iso || (dayEvents[0] && dayEvents[0].tarihIso) || '',
                label: label.day ? label : null,
                events: dayEvents,
            }
        }).sort((a, b) => Number(!a.label) - Number(!b.label))
    }, [program, events, year])

    const counts = useMemo(() => {
        const result = { all: events.length }
        CATEGORIES.forEach((category) => { result[category.id] = 0 })
        events.forEach((show) => { result[categoryOf(show)] += 1 })
        return result
    }, [events])

    const visibleDays = days
        .map((day) => ({
            ...day,
            events: filter === 'all' ? day.events : day.events.filter((show) => categoryOf(show) === filter),
        }))
        .filter((day) => day.events.length)

    const today = todayIso()
    const isOver = isProgramOver(year)
    const isPastDay = (day) => !isOver && Boolean(day.iso) && day.iso < today
    const labelledDays = days.filter((day) => day.label).map((day) => day.label)
    const hasRail = visibleDays.some((day) => day.label)
    const visibleKey = visibleDays.map((day) => day.id).join('|')

    useEffect(() => {
        if (!hasRail || typeof IntersectionObserver === 'undefined') return undefined
        const nodes = visibleKey.split('|').map((id) => document.getElementById(id)).filter(Boolean)
        const observer = new IntersectionObserver((entries) => {
            const hit = entries.filter((entry) => entry.isIntersecting)
                .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top)[0]
            if (hit) setActiveDay(hit.target.id)
        }, { rootMargin: '-120px 0px -55% 0px' })
        nodes.forEach((node) => observer.observe(node))
        return () => observer.disconnect()
    }, [hasRail, visibleKey])

    useEffect(() => {
        const rail = railRef.current
        if (!rail || !activeDay) return
        const button = rail.querySelector(`[data-day="${activeDay}"]`)
        if (!button) return
        rail.scrollTo({ left: button.offsetLeft - rail.clientWidth / 2 + button.offsetWidth / 2, behavior: 'smooth' })
    }, [activeDay])

    const jumpTo = (id) => {
        const node = document.getElementById(id)
        if (!node) return
        const reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches
        node.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'start' })
        setActiveDay(id)
    }

    const detailHref = (show) => (
        filled(show.id) ? `/oyun?f=${year}&id=${show.id}` : `/festival?f=${year}`
    )

    const ticketBlock = (show) => {
        if (!filled(show.aciklama) && program.emptyDescriptionNote) {
            return <p className='fp-notice'>{program.emptyDescriptionNote}</p>
        }
        if (isOver || !filled(show.bilet)) return null
        if (show.bilet === 'free') {
            return (
                <>
                    <span className='fp-free'>Ücretsiz</span>
                    <p className='fp-action-note'>Biletler 10 Ekim itibariyle {show.salon}'inden temin edilebilir.</p>
                </>
            )
        }
        if (show.bilet === '-') {
            return <span className='fp-free'>{show.fiyat || 'Ücretsiz'}</span>
        }
        return (
            <a className='fp-ticket' href={show.bilet} target='_blank' rel='noreferrer'>
                Bilet al <span aria-hidden='true'>→</span>
            </a>
        )
    }

    const eventCard = (show, key) => {
        const category = categoryOf(show)
        const tag = filled(show.tur) ? capitalize(show.tur) : CATEGORIES.find((c) => c.id === category).tag
        const time = splitTime(show.saat)
        const { summary, capacity } = describe(show.aciklama)
        const place = show.sehir || show.ulke
        const showCompany = filled(show.topluluk) && lower(show.topluluk) !== lower(show.tur)
        const credits = [
            ['Yazan', show.yazar],
            ['Yöneten', show.yonetmen],
            ['Uyarlayan', show.uyarlayan],
            ['Konuşmacılar', show.konusmaci],
            ['Müzik', show.muzisyen],
        ].filter(([, value]) => filled(value))
        const href = detailHref(show)

        return (
            <article key={key} className={`fp-event fp-event--${category}`}>
                <div className='fp-time'>
                    {time.start ? <span className='fp-time-start'>{time.start}</span> : null}
                    {time.end ? <span className='fp-time-end'>– {time.end}</span> : null}
                </div>

                <Link to={href} className='fp-poster' tabIndex={-1} aria-hidden='true'>
                    {show.afis ? (
                        <img src={posterUrl(festival, show.afis)} alt='' loading='lazy' />
                    ) : (
                        <span className='fp-poster-empty'>{tag}</span>
                    )}
                </Link>

                <div className='fp-body'>
                    <p className='fp-tag'>{tag}</p>
                    <h3 className='fp-title'>
                        <Link to={href}>{show.oyun}</Link>
                    </h3>
                    {showCompany ? (
                        <p className='fp-company'>
                            {show.topluluk}
                            {place ? <span className='fp-place'> · {place}</span> : null}
                        </p>
                    ) : null}
                    {credits.length ? (
                        <dl className='fp-credits'>
                            {credits.map(([label, value]) => (
                                <div key={label}>
                                    <dt>{label}</dt>
                                    <dd>{value}</dd>
                                </div>
                            ))}
                        </dl>
                    ) : null}
                    {summary ? <p className='fp-summary'>{summary}</p> : null}
                    {show.gosteriler && show.gosteriler.length ? (
                        <ul className='fp-sublist'>
                            {show.gosteriler.map((sub, subKey) => (
                                <li key={subKey}>
                                    <strong>{sub.oyun}</strong>
                                    {sub.topluluk ? <span> · {sub.topluluk}{sub.sehir ? ` ${sub.sehir}` : ''}</span> : null}
                                    {sub.tur ? <span className='fp-sublist-tur'> · {capitalize(sub.tur)}</span> : null}
                                </li>
                            ))}
                        </ul>
                    ) : null}
                    <div className='fp-meta'>
                        {show.salon ? (
                            <span className='fp-sign'>
                                <span className='fp-sign-arrow' aria-hidden='true'>←</span>
                                <span className='sr-only'>Mekân: </span>
                                {show.salon}
                            </span>
                        ) : null}
                        {show.sure ? <span className='fp-fact'>{show.sure}</span> : null}
                        {capacity ? <span className='fp-fact'>Kontenjan {capacity}</span> : null}
                        {show.fiyat && show.bilet !== '-' ? <span className='fp-fact'>{show.fiyat}</span> : null}
                    </div>
                </div>

                <div className='fp-action'>
                    {ticketBlock(show)}
                    <Link to={href} className='fp-more'>Ayrıntılar</Link>
                </div>
            </article>
        )
    }

    const galleryCard = (show, key) => {
        const category = categoryOf(show)
        const href = detailHref(show)
        const html = looksLikeHtml(show.aciklama)
        const showCompany = filled(show.topluluk) && lower(show.topluluk) !== lower(show.oyun)
        const place = show.sehir || show.ulke

        return (
            <article key={key} className={`fp-tile fp-event--${category}`}>
                <Link to={href} className='fp-tile-poster' tabIndex={-1} aria-hidden='true'>
                    {show.afis ? (
                        <img src={posterUrl(festival, show.afis)} alt='' loading='lazy' />
                    ) : (
                        <span className='fp-poster-empty'>{show.oyun}</span>
                    )}
                </Link>
                <div className='fp-tile-body'>
                    {filled(show.tur) ? <p className='fp-tag'>{capitalize(show.tur)}</p> : null}
                    <h3 className='fp-title'>
                        <Link to={href}>{show.oyun}</Link>
                    </h3>
                    {showCompany ? (
                        <p className='fp-company'>
                            {show.topluluk}
                            {place ? <span className='fp-place'> · {place}</span> : null}
                        </p>
                    ) : null}
                    {filled(show.aciklama) ? (
                        html
                            ? <div className='fp-tile-copy' dangerouslySetInnerHTML={{ __html: show.aciklama }} />
                            : <p className='fp-tile-copy'>{describe(show.aciklama).summary}</p>
                    ) : null}
                    {show.salon ? (
                        <div className='fp-meta'>
                            <span className='fp-sign'>
                                <span className='fp-sign-arrow' aria-hidden='true'>←</span>
                                <span className='sr-only'>Mekân: </span>
                                {show.salon}
                            </span>
                        </div>
                    ) : null}
                </div>
            </article>
        )
    }

    const isGallery = !(program.dates || []).length
    const activeCategories = CATEGORIES.filter((category) => counts[category.id]).length

    return (
        <section className='fp' id={`program-${year}`} lang='tr' aria-labelledby={`fp-heading-${year}`}>
            <div className='container'>
                <header className='fp-head'>
                    <div className='fp-head-text'>
                        <p className='fp-eyebrow'>
                            {festival.name ? `${festival.name} · ` : ''}Program
                        </p>
                        <h2 className='fp-heading' id={`fp-heading-${year}`}>
                            {upper(dateRange(labelledDays) || 'Festival programı')}
                        </h2>
                        <p className='fp-summary-line'>
                            {events.length} etkinlik{labelledDays.length ? `, ${labelledDays.length} gün` : ''}
                        </p>
                    </div>
                    {festival.pdf || (program.ticketListUrl && !isOver) ? (
                        <div className='fp-head-links'>
                            {festival.pdf ? (
                                <a href={festival.pdf} target='_blank' rel='noreferrer' className='fp-head-link'>
                                    Programı PDF olarak indir
                                </a>
                            ) : null}
                            {program.ticketListUrl && !isOver ? (
                                <a href={program.ticketListUrl} target='_blank' rel='noreferrer' className='fp-head-link'>
                                    Tüm biletler
                                </a>
                            ) : null}
                        </div>
                    ) : null}
                </header>

                {activeCategories > 1 ? (
                <div className='fp-filters' role='group' aria-label='Etkinlik türüne göre süz'>
                    {[{ id: 'all', label: 'Tümü' }, ...CATEGORIES].filter((c) => counts[c.id]).map((category) => (
                        <button
                            key={category.id}
                            type='button'
                            className={`fp-filter fp-filter--${category.id}`}
                            aria-pressed={filter === category.id}
                            onClick={() => setFilter(category.id)}
                        >
                            {category.label}
                            <span className='fp-filter-count'>{counts[category.id]}</span>
                        </button>
                    ))}
                </div>
                ) : null}
            </div>

            {hasRail ? (
                <nav className='fp-rail' aria-label='Program günleri'>
                    <div className='container'>
                        <div className='fp-rail-track' ref={railRef}>
                            {visibleDays.filter((day) => day.label).map((day) => (
                                <button
                                    key={day.id}
                                    type='button'
                                    data-day={day.id}
                                    className={`fp-rail-day${activeDay === day.id ? ' is-active' : ''}${isPastDay(day) ? ' is-past' : ''}`}
                                    aria-current={activeDay === day.id ? 'true' : undefined}
                                    onClick={() => jumpTo(day.id)}
                                >
                                    <span className='fp-rail-num'>{day.label.day}</span>
                                    <span className='fp-rail-wd'>{day.label.short}</span>
                                </button>
                            ))}
                        </div>
                    </div>
                </nav>
            ) : null}

            <div className='container'>
                {isGallery ? (
                    <div className='fp-gallery'>
                        {visibleDays.flatMap((day) => day.events).map((show, key) => galleryCard(show, key))}
                    </div>
                ) : (
                <div className='fp-days'>
                    {visibleDays.map((day) => (
                        <section
                            key={day.id}
                            id={day.id}
                            className={`fp-day${day.label ? '' : ' fp-day--plain'}${isPastDay(day) ? ' is-past' : ''}${day.iso === today ? ' is-today' : ''}`}
                            aria-label={day.label ? `${day.label.day} ${day.label.month} ${day.label.weekday}` : undefined}
                        >
                            {day.label ? (
                                <div className='fp-day-head'>
                                    <div className='fp-day-stick'>
                                        <span className='fp-day-num'>{day.label.day}</span>
                                        <span className='fp-day-month'>{day.label.month}</span>
                                        <span className='fp-day-wd'>{day.label.weekday}</span>
                                        {day.iso === today ? <span className='fp-day-badge'>Bugün</span> : null}
                                    </div>
                                </div>
                            ) : null}
                            <div className='fp-day-list'>
                                {day.events.map((show, key) => eventCard(show, key))}
                            </div>
                        </section>
                    ))}
                </div>
                )}
            </div>
        </section>
    )
}
